import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getListingById, publishListing, deleteListing } from '@/lib/queries/listings'
import type { ListingDetail } from '@/lib/queries/listings'
import { useState } from 'react'
import toast from 'react-hot-toast'

/**
 * Listing Detail page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: listing-detail.html
 */
export default function ListingDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMenu, setShowMenu] = useState(false)

  const { data: listing, isLoading, error } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => getListingById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  const { mutateAsync: doPublish, isPending: isPublishing } = useMutation({
    mutationFn: () => publishListing(id!),
    onSuccess: () => {
      // Update cache immediately
      queryClient.setQueryData(['listing', id], (prev: any) => {
        if (!prev) return prev
        return { ...prev, status: 'synced', linkedAt: new Date().toISOString() }
      })
      // Keep optimistic value; refetch list only
      queryClient.invalidateQueries({ queryKey: ['listings'] })
    },
  })

  const { mutateAsync: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: () => {
      if (!id) {
        throw new Error('Listing ID is required')
      }
      return deleteListing(id)
    },
    onSuccess: async () => {
      // Cancel any in-flight queries to prevent stale data
      await queryClient.cancelQueries({ queryKey: ['listings'] })
      // Remove all listing-related queries from cache completely
      queryClient.removeQueries({ queryKey: ['listing', id] })
      queryClient.removeQueries({ queryKey: ['listings'] })
      queryClient.removeQueries({ queryKey: ['listingsByProduct'] })
      queryClient.removeQueries({ queryKey: ['listings', 'active'] })
      // Navigate immediately - component will refetch fresh data on mount
      navigate('/listings')
    },
    onError: (error) => {
      // Log error for debugging
      console.error('Failed to delete listing:', error)
      // Error is handled in handleDelete catch block
    },
  })

  const handleDelete = async () => {
    if (!id) return
    const ok = window.confirm('Are you sure you want to delete this listing? This cannot be undone.')
    if (!ok) return
    setShowMenu(false)
    
    // Show loading toast when deletion starts
    const loadingToast = toast.loading('Deleting listing...')
    
    try {
      await doDelete()
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast)
      toast.success('Listing deleted successfully')
    } catch (error) {
      // Dismiss loading toast and show error
      toast.dismiss(loadingToast)
      toast.error(`Failed to delete listing: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Helper to format price (GBP)
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'N/A'
    return `£${price.toFixed(2)}`
  }

  // Helper to get listing image URL
  const getListingImageUrl = (listing: ListingDetail): string | null => {
    const product = listing.relatedProduct
    if (!product?.media) return null

    // Try hero image first
    if (product.media.hero?.url) {
      return product.media.hero.url
    }
    // Try first gallery image
    if (product.media.galleryImages && product.media.galleryImages.length > 0) {
      return product.media.galleryImages[0].url || null
    }
    return null
  }

  // Helper to get channel display name
  const getChannelDisplayName = (channelCode: string | null | undefined): string => {
    if (!channelCode) return 'Unknown'
    return channelCode.charAt(0).toUpperCase() + channelCode.slice(1)
  }

  // Helper to get status display
  const getStatusDisplay = (status: string): { text: string; isActive: boolean } => {
    switch (status) {
      case 'synced':
      case 'enabled':
        return { text: 'Active', isActive: true }
      case 'not_synced':
      case 'disabled':
        return { text: 'Draft', isActive: false }
      case 'draft':
        return { text: 'Draft', isActive: false }
      case 'error':
        return { text: 'Error', isActive: false }
      case 'pending':
        return { text: 'Pending', isActive: false }
      default:
        return { text: status, isActive: false }
    }
  }

  // Consider listing published if it has an externalListingId (exists on channel)
  // OR if status is synced with all required fields
  const isPublished = !!(
    listing &&
    (
      listing.externalListingId || // If it has an external ID, it's been imported and exists on the channel
      (listing.status === 'synced' && listing.channel && listing.linkedAt)
    )
  )

  // Helper to check if price is overridden
  const hasPriceOverride = (listing: ListingDetail): boolean => {
    const productRrp = listing.relatedProduct?.rrp
    const listingPrice = listing.price
    // If listing price is N/A (undefined/null), it's not overridden
    if (listingPrice === undefined || listingPrice === null) return false
    // If product RRP is not available, can't determine override
    if (productRrp === undefined || productRrp === null) return false
    // Only overridden if both exist and they're different
    return listingPrice !== productRrp
  }

  // Helper to render description (portable text or string)
  const renderDescription = (description: any): string => {
    if (!description) return ''
    if (typeof description === 'string') return description
    if (Array.isArray(description)) {
      // Portable text array - extract text content
      return description
        .map((block: any) => {
          if (block._type === 'block' && block.children) {
            return block.children.map((child: any) => child.text || '').join('')
          }
          return ''
        })
        .join('\n')
    }
    return ''
  }

  // Helper to check if title is overridden
  const hasTitleOverride = (listing: ListingDetail): boolean => {
    const listingTitle = listing.title
    const productTitle = listing.relatedProduct?.title
    if (!listingTitle || !productTitle) return false
    return listingTitle !== productTitle
  }

  // Helper to check if description is overridden
  const hasDescriptionOverride = (listing: ListingDetail): boolean => {
    const listingDesc = renderDescription(listing.description)
    const productDesc = renderDescription(listing.relatedProduct?.description)
    if (!listingDesc || !productDesc) return false
    return listingDesc !== productDesc
  }

  // Helper to get channel URL (placeholder - would need channel-specific URL construction)
  const getChannelUrl = (listingArg: ListingDetail): string | null => {
    // Reference until implemented to satisfy no-unused-vars
    void listingArg
    // TODO: Construct channel-specific URL based on channel type and externalListingId
    // For now, return null
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading listing...</div>
      </div>
    )
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Listing not found</p>
          <Link
            to="/listings"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44"
          >
            Back to List
          </Link>
        </div>
      </div>
    )
  }

  const imageUrl = getListingImageUrl(listing)
  const statusDisplay = getStatusDisplay(listing.status)
  const hasOverride = hasPriceOverride(listing)
  const hasTitleOver = hasTitleOverride(listing)
  const hasDescOver = hasDescriptionOverride(listing)
  const product = listing.relatedProduct
  const listingTitle = listing.title || product?.title || 'Untitled Listing'
  const listingDescription = renderDescription(listing.description) || renderDescription(product?.description) || ''
  const productTitle = product?.title || 'Unknown Product'
  const productSku = product?.sku || 'No SKU'
  const basePrice = product?.cost
  const productRrp = product?.rrp
  const productId = product?._id
  const channelUrl = getChannelUrl(listing)
  // If listing price is N/A, use product RRP as the displayed price
  const displayPrice = listing.price !== undefined && listing.price !== null ? listing.price : productRrp

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Listing Detail</h1>
          <div className="flex gap-3">
            <Link
              to={`/listings/${id}/edit`}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="Edit"
            >
              ✏️
            </Link>
            {listing?.status === 'not_synced' && (
              <button
                className="px-3 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-blue-700 transition-colors disabled:opacity-50"
                onClick={() => doPublish()}
                disabled={isPublishing}
              >
                Publish
              </button>
            )}
            <div className="relative">
              <button
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
                aria-label="More options"
                onClick={() => setShowMenu(!showMenu)}
              >
                ⋯
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <button
                      type="button"
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors touch-target-min-44"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Listing'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Listing Image */}
        <div className="w-full aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={listingTitle}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-6xl">🖼️</span>
          )}
        </div>

        {/* Listing Info Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">{listingTitle}</h2>
          <div className="flex gap-2 flex-wrap mb-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
              {getChannelDisplayName(listing.channel)}
            </span>
            <span
              className={`px-3 py-1 rounded text-xs font-medium ${
                statusDisplay.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {statusDisplay.text}
            </span>
            {hasOverride && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                Price Override
              </span>
            )}
          </div>
          <div className="text-3xl font-bold text-blue-600 mb-6">
            {formatPrice(displayPrice)}
          </div>

          {/* Status */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Status</div>
            {isPublished ? (
              <div className="text-base text-gray-900">
                {statusDisplay.text} • Published to {getChannelDisplayName(listing.channel || 'channel')}
              </div>
            ) : (
              <div className="text-base text-gray-900">
                {statusDisplay.text} • Not published{listing.channel ? ` to ${getChannelDisplayName(listing.channel)}` : ''}
              </div>
            )}
          </div>

          {/* Channel Listing ID */}
          {listing.externalListingId && (
            <div className="mb-4">
              <div className="text-xs text-gray-600 mb-1">Channel Listing ID</div>
              <div className="text-base text-gray-900">
                {getChannelDisplayName(listing.channel)} Listing #{listing.externalListingId}
              </div>
            </div>
          )}
        </div>

        {/* Product Information Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Information</h3>
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Product</div>
            {productId ? (
              <Link
                to={`/products/${productId}`}
                className="text-base text-blue-600 hover:text-blue-700 font-medium"
              >
                {productTitle} ({productSku})
              </Link>
            ) : (
              <div className="text-base text-gray-900">
                {productTitle} ({productSku})
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-600 mb-1">Base Price</div>
              <div className="text-base text-gray-900">{formatPrice(basePrice)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">RRP</div>
              <div className="text-base text-gray-900">{formatPrice(productRrp)}</div>
            </div>
          </div>
        </div>

        {/* Listing Overrides Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Listing Overrides</h3>

          {/* Title */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Title</div>
            <div className="text-base text-gray-900">{listingTitle}</div>
            {!hasTitleOver ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                Using product title (no override)
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                Overridden from product title
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Description</div>
            <div className="text-base text-gray-900 whitespace-pre-wrap">
              {listingDescription || 'No description'}
            </div>
            {!hasDescOver ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                Using product description (no override)
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                Overridden from product description
              </div>
            )}
          </div>

          {/* Price */}
          <div>
            <div className="text-xs text-gray-600 mb-1">Price</div>
            <div className="text-base text-gray-900">{formatPrice(displayPrice)}</div>
            {listing.price !== undefined && listing.price !== null ? (
              hasOverride && productRrp !== undefined ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                  Overridden from {formatPrice(productRrp)}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                  Using product RRP (no override)
                </div>
              )
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mt-2 text-xs text-yellow-800">
                Using product RRP (no override)
              </div>
            )}
          </div>
        </div>

        {/* Channel Link Card */}
        {channelUrl && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Channel Link</h3>
            <div className="text-sm text-gray-600 mb-3">
              View this listing on {getChannelDisplayName(listing.channel)}
            </div>
            <a
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm text-center bg-white hover:bg-gray-50 transition-colors touch-target-min-44"
            >
              Open in {getChannelDisplayName(listing.channel)}
            </a>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 mb-24">
          <Link
            to="/listings"
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to List
          </Link>
          {isPublished ? (
            <Link
              to={`/listings/${id}/edit`}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-blue-700 transition-colors"
            >
              Update Listing
            </Link>
          ) : (
            <button
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors disabled:opacity-50"
              onClick={() => doPublish()}
              disabled={isPublishing}
            >
              Publish Listing
            </button>
          )}
        </div>
      </main>
    </div>
  )
}
